import { Globe } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AUTO_TIMEZONE,
  COMMON_TIMEZONES,
  getBrowserTimezone,
  resolveTimezone,
  setTimezonePreference,
} from "@/lib/timezonePreference";
import { useTimezonePreference } from "@/hooks/useTimezonePreference";

/**
 * Compact timezone picker used on the Logs page header and anywhere
 * else activity timestamps are surfaced. Selecting a value persists it
 * via `setTimezonePreference`, which fires the
 * `timezone-preference-updated` event so subscribed components
 * (`useTimezonePreference`) re-render immediately.
 */
interface TimezoneSelectProps {
  className?: string;
  /** Optional aria-label override; defaults to "Activity timestamp timezone". */
  ariaLabel?: string;
}

export function TimezoneSelect({ className, ariaLabel }: TimezoneSelectProps) {
  const pref = useTimezonePreference();
  const browserZone = getBrowserTimezone();
  const resolved = resolveTimezone(pref);

  return (
    <Select value={pref} onValueChange={(v) => setTimezonePreference(v)}>
      <SelectTrigger
        className={className ?? "w-[220px]"}
        aria-label={ariaLabel ?? "Activity timestamp timezone"}
        title={`Timestamps shown in ${resolved}`}
      >
        <Globe className="h-4 w-4 mr-2 text-muted-foreground" aria-hidden="true" />
        <SelectValue placeholder="Timezone" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectLabel>Default</SelectLabel>
          <SelectItem value={AUTO_TIMEZONE}>
            Browser ({browserZone})
          </SelectItem>
        </SelectGroup>
        <SelectGroup>
          <SelectLabel>Common timezones</SelectLabel>
          {COMMON_TIMEZONES.map((tz) => (
            <SelectItem key={tz.value} value={tz.value}>
              {tz.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

export default TimezoneSelect;
